import * as Yup from 'yup'
import { cloneDeep } from 'lodash-es'
import { EmailConfig } from './types'

export enum EMAIL_CONFIG_FORM_FIELDS {
  EMAIL_ID = 'emailId',
  SYSTEM_MAPPING_ID = 'systemMappingId',
  USERNAME = 'username',
  APP_PASSWORD = 'appPassword',
  SMTP_SERVER = 'smtpServer',
  PORT = 'port',
  PROTOCOL = 'protocol',
  OBJECT_TYPE = 'objectType',
  NOTIFICATION_EMAILS = 'notificationEmails',
  ENABLE = 'enable'
}


export enum EMAIL_OBJECT_TYPES {
  PAYMENT_REMITTANCE = 'PAYMENT_REMITTANCE',
  INVOICE = 'INVOICE'
}

export interface EmailConfigForm {
  [EMAIL_CONFIG_FORM_FIELDS.EMAIL_ID]: string
  [EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID]: number | string
  [EMAIL_CONFIG_FORM_FIELDS.USERNAME]: string
  [EMAIL_CONFIG_FORM_FIELDS.APP_PASSWORD]: string
  [EMAIL_CONFIG_FORM_FIELDS.SMTP_SERVER]: string
  [EMAIL_CONFIG_FORM_FIELDS.PORT]: number | string
  [EMAIL_CONFIG_FORM_FIELDS.PROTOCOL]: string
  [EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE]: string
  [EMAIL_CONFIG_FORM_FIELDS.NOTIFICATION_EMAILS]?: string
  [EMAIL_CONFIG_FORM_FIELDS.ENABLE]: boolean
}


export const defaultEmailConfigFormValues: EmailConfigForm = {
  [EMAIL_CONFIG_FORM_FIELDS.EMAIL_ID]: '',
  [EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID]: '',
  [EMAIL_CONFIG_FORM_FIELDS.USERNAME]: '',
  [EMAIL_CONFIG_FORM_FIELDS.APP_PASSWORD]: '',
  [EMAIL_CONFIG_FORM_FIELDS.SMTP_SERVER]: '',
  [EMAIL_CONFIG_FORM_FIELDS.PORT]: '',
  [EMAIL_CONFIG_FORM_FIELDS.PROTOCOL]: '',
  [EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE]: '',
  [EMAIL_CONFIG_FORM_FIELDS.NOTIFICATION_EMAILS]: '',
  [EMAIL_CONFIG_FORM_FIELDS.ENABLE]: true
}


export const EmailConfigFormLabels: Record<string, string> = {
  [EMAIL_CONFIG_FORM_FIELDS.EMAIL_ID]: 'Email ID',
  [EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID]: 'System Mapping ID',
  [EMAIL_CONFIG_FORM_FIELDS.USERNAME]: 'Username',
  [EMAIL_CONFIG_FORM_FIELDS.APP_PASSWORD]: 'App Password',
  [EMAIL_CONFIG_FORM_FIELDS.SMTP_SERVER]: 'SMTP Server',
  [EMAIL_CONFIG_FORM_FIELDS.PORT]: 'Port',
  [EMAIL_CONFIG_FORM_FIELDS.PROTOCOL]: 'Protocol',
  [EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE]: 'Object Type',
  [EMAIL_CONFIG_FORM_FIELDS.NOTIFICATION_EMAILS]: 'Notification Emails',
  [EMAIL_CONFIG_FORM_FIELDS.ENABLE]: 'Enable'
}

export const getEmailConfigValidationSchema = Yup.object().shape({
  [EMAIL_CONFIG_FORM_FIELDS.EMAIL_ID]: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),

  [EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID]: Yup.number()
    .typeError('System Mapping ID must be a number')
    .required('System Mapping ID is required'),

  [EMAIL_CONFIG_FORM_FIELDS.USERNAME]: Yup.string()
    .required('Username is required'),

  [EMAIL_CONFIG_FORM_FIELDS.APP_PASSWORD]: Yup.string()
    .required('App Password is required'),

  [EMAIL_CONFIG_FORM_FIELDS.SMTP_SERVER]: Yup.string()
    .required('SMTP Server is required'),

  [EMAIL_CONFIG_FORM_FIELDS.PORT]: Yup.number()
    .typeError('Port must be a number')
    .required('Port is required'),

  [EMAIL_CONFIG_FORM_FIELDS.PROTOCOL]: Yup.string()
    .required('Protocol is required'),

  [EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE]: Yup.mixed<EMAIL_OBJECT_TYPES>()
    .oneOf(Object.values(EMAIL_OBJECT_TYPES))
    .required('Object Type is required'),

  [EMAIL_CONFIG_FORM_FIELDS.NOTIFICATION_EMAILS]: Yup.string()
    .test(
      'email-list',
      'Enter valid comma-separated email addresses',
      function (value) {
        if (!value) return true
        const emails = value.split(',')
        for (const email of emails) {
          const trimmedEmail = email.trim()
          if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            return this.createError({
              message: `${trimmedEmail} is not a valid email address`
            })
          }
        }
        return true
      }
    )
    .notRequired()
})


export const getInitialEmailConfigFormValueFromResponse = (
  response: Partial<EmailConfig>
): EmailConfigForm => {
  const formInitialValues: EmailConfigForm = {
    ...defaultEmailConfigFormValues,
    ...response,
    [EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE]: response.objectType as string,
    [EMAIL_CONFIG_FORM_FIELDS.ENABLE]: Boolean(response?.enable)
  }

  return cloneDeep(formInitialValues)
}
